import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, AlertTriangle } from "lucide-react";

import { fabricMatches } from "@/lib/cutTickets";
import { bomGaps } from "@/lib/season";

const low = (x) => (x || "").trim().toLowerCase();
const fabricOpts = (mats, type) => fabricMatches(mats, type);
const accOpts = (mats, name) => {
  const acc = mats.filter((m) => m.type !== "tessuto");
  const same = acc.filter((m) => low(m.name) === low(name));
  return same.length ? same : acc;
};
const sorted = (opts, color) => [...opts.filter((m) => low(m.color) === low(color)), ...opts.filter((m) => low(m.color) !== low(color))];
const auto = (opts, color) => { const s = opts.filter((m) => low(m.color) === low(color)); return s.length === 1 ? s[0].id : ""; };
const label = (m) => [m.name, m.color, m.supplier].filter(Boolean).join(" · ");

const Cell = ({ opts, color, value, onChange, testid }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className={`h-8 min-w-[170px] text-xs ${value ? "" : "text-muted-foreground"}`} data-testid={testid}><SelectValue placeholder={opts.length ? "Scegli" : "Niente in magazzino"} /></SelectTrigger>
    <SelectContent>{sorted(opts, color).map((m) => <SelectItem key={m.id} value={m.id}>{label(m)}</SelectItem>)}</SelectContent>
  </Select>
);

const ColorsBar = ({ colors, suggestions, onAdd, onRemove }) => {
  const [v, setV] = useState("");
  const add = () => { const c = v.trim(); if (c && !colors.some((x) => low(x) === low(c))) onAdd(c); setV(""); };
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      {colors.map((c) => (
        <span key={c} className="flex items-center gap-1 rounded-full border border-border bg-secondary px-3 py-1 text-xs" data-testid={`bom-color-${c}`}>
          {c}<button type="button" onClick={() => onRemove(c)} className="text-muted-foreground hover:text-rose-400" data-testid={`bom-color-remove-${c}`}><X className="h-3 w-3" /></button>
        </span>
      ))}
      <datalist id="bom-color-list">{suggestions.map((c) => <option key={c} value={c} />)}</datalist>
      <Input list="bom-color-list" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Aggiungi un colore (es. Nero)" className="h-8 w-52" data-testid="bom-color-input" />
      <Button size="sm" variant="outline" className="h-8" onClick={add} data-testid="bom-color-add"><Plus className="mr-1 h-3.5 w-3.5" />Colore</Button>
    </div>
  );
};

// Distinta base della scheda tecnica: quantità una volta sola, materiale esatto per ogni colore
export const BomSection = ({ tech, setTech, listColors = [] }) => {
  const { data: mats = [] } = useQuery({ queryKey: ["materials"], queryFn: async () => (await api.get("/materials")).data });
  const colors = tech.colors || [];
  const gaps = new Set(bomGaps(tech).map((g) => g.color));
  const fabrics = (tech.fabrics || []).map((f, i) => ({ f, i })).filter(({ f }) => (f.fabric_type || "").trim());
  const accs = (tech.accessories || []).map((a, i) => ({ a, i })).filter(({ a }) => (a.name || "").trim());
  const suggestions = [...new Set([...listColors, ...mats.map((m) => m.color).filter(Boolean)])].sort();
  const setRow = (key, i, patch) => setTech({ [key]: tech[key].map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const setPick = (key, i, color, id) => setRow(key, i, { by_color: { ...(tech[key][i].by_color || {}), [color]: id } });
  const addColor = (c) => setTech({
    colors: [...colors, c],
    fabrics: tech.fabrics.map((f) => (f.fabric_type ? { ...f, by_color: { ...(f.by_color || {}), [c]: auto(fabricOpts(mats, f.fabric_type), c) } } : f)),
    accessories: tech.accessories.map((a) => (a.name ? { ...a, by_color: { ...(a.by_color || {}), [c]: auto(accOpts(mats, a.name), c) } } : a)),
  });
  const drop = (r, c) => { const { [c]: _, ...rest } = r.by_color || {}; return { ...r, by_color: rest }; };
  const removeColor = (c) => setTech({ colors: colors.filter((x) => x !== c), fabrics: tech.fabrics.map((f) => drop(f, c)), accessories: tech.accessories.map((a) => drop(a, c)) });
  const th = "p-2 text-left text-xs font-medium text-muted-foreground";
  return (
    <div data-testid="tech-bom">
      <ColorsBar colors={colors} suggestions={suggestions} onAdd={addColor} onRemove={removeColor} />
      {bomGaps(tech).length > 0 && (
        <div className="mb-3 rounded-md border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs text-amber-400" data-testid="bom-gaps">
          <p className="font-semibold">Distinta incompleta: materiali ancora da scegliere</p>
          {bomGaps(tech).map((g) => <p key={g.color} data-testid={`bom-gap-${g.color}`}><b>{g.color}</b>: {g.missing.join(", ")}</p>)}
        </div>
      )}
      {!fabrics.length && !accs.length ? <p className="text-xs text-muted-foreground">Scrivi prima i tessuti e gli accessori del capo: qui li trovi come righe della distinta.</p> : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border"><th className={th}>Materiale</th><th className={`${th} w-28`}>Per capo</th>{colors.map((c) => { const gap = gaps.has(c); return <th key={c} className={`${th} ${gap ? "text-amber-400" : ""}`} data-testid={`bom-col-${c}`}><span className="inline-flex items-center gap-1">{c}{gap && <AlertTriangle className="h-3 w-3" />}</span></th>; })}</tr></thead>
            <tbody>
              {fabrics.map(({ f, i }, n) => (
                <tr key={`f${i}`} className={`border-b border-border/50 ${n % 2 ? "bg-secondary/60" : ""}`}>
                  <td className="p-2"><span className="font-medium">{f.fabric_type}</span><span className="block text-[11px] text-muted-foreground">tessuto</span></td>
                  <td className="p-1"><div className="flex items-center gap-1"><Input value={f.meters_per_piece ?? ""} onChange={(e) => setRow("fabrics", i, { meters_per_piece: e.target.value })} inputMode="decimal" placeholder="0" className="h-8 w-16 text-center" data-testid={`bom-fabric-${i}-qty`} /><span className="text-xs text-muted-foreground">m</span></div></td>
                  {colors.map((c) => <td key={c} className="p-1"><Cell opts={fabricOpts(mats, f.fabric_type)} color={c} value={f.by_color?.[c]} onChange={(id) => setPick("fabrics", i, c, id)} testid={`bom-fabric-${i}-${c}`} /></td>)}
                </tr>
              ))}
              {accs.map(({ a, i }, n) => (
                <tr key={`a${i}`} className={`border-b border-border/50 last:border-0 ${(fabrics.length + n) % 2 ? "bg-secondary/60" : ""}`}>
                  <td className="p-2"><span className="font-medium">{a.name}</span><span className="block text-[11px] text-muted-foreground">accessorio</span></td>
                  <td className="p-1"><Input value={a.quantity} onChange={(e) => setRow("accessories", i, { quantity: e.target.value })} placeholder="es. 6" className="h-8 w-20 text-center" data-testid={`bom-acc-${i}-qty`} /></td>
                  {colors.map((c) => <td key={c} className="p-1"><Cell opts={accOpts(mats, a.name)} color={c} value={a.by_color?.[c]} onChange={(id) => setPick("accessories", i, c, id)} testid={`bom-acc-${i}-${c}`} /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {colors.length === 0 && (fabrics.length > 0 || accs.length > 0) && <p className="mt-2 text-xs text-muted-foreground">Aggiungi i colori che vuoi produrre: per ognuno scegli il tessuto e l'accessorio esatto dal magazzino.</p>}
    </div>
  );
};
